/**
 * idempotency-lru · hybrid time-and-count-bounded LRU of recent mutation
 * idempotency keys.
 *
 * SPEC ANCHOR: `specs/009-data-fetching-audit/05-new-infra-sdk-changes.html`
 * §L2377 (Idempotency keys) + §L2387 (Why count-bounded, not time-bounded).
 *
 * SHAPE
 * -----
 *   entry value : { completedAt: number }       // ms epoch
 *   key         : string                        // UUIDv4 from crypto.randomUUID
 *   hard cap    : 4096 entries (≈ 200 KB ceiling, LRU eviction)
 *   min retain  : 90_000 ms from `completedAt`  // covers Solana p99.9 finality
 *                                               // + indexer flush
 *
 * Pure count-only eviction races: a > 1024 same-tab burst could evict an entry
 * before its NATS event arrives. Pure time-only lets memory grow unbounded
 * under high cardinality. Hybrid closes both.
 *
 * Solana finality during congestion (priority-fee spikes, leader skips)
 * regularly hits 60-120 s p99.9. A wall-clock dedup window short enough to be
 * useful (60 s) would frequently let stale events through; a window long
 * enough to cover congestion (10 m) would consume meaningful memory at scale.
 * Bounding by count keeps memory predictable and makes congestion irrelevant.
 *
 * UUIDv4 collision probability is mathematically negligible, so the key alone
 * is sufficient — `txSlot` ships in payloads for telemetry/debug only.
 *
 * LIFECYCLE
 * ---------
 *   insert  : `onMutate` calls `record(key)` with `completedAt = now` — covers
 *             cancelled mutations that never reach `onSettled` (component
 *             unmount mid-flight, route change abort, paused mutation dropped
 *             by TanStack on observer destroy). The 90 s floor measures from
 *             INSERT, not completion.
 *   settle  : `onSettled` overwrites `completedAt = now` so long-running
 *             mutations get the full 90 s post-settle window.
 *   evict   : count > 4096 AND now - completedAt > 90 s.
 *   cleanup : useEffect cleanup in the wrapper stamps `completedAt` for any
 *             in-flight keys observed by the hook instance.
 *
 * CROSS-TAB
 * ---------
 * Each tab maintains its own LRU. Sibling tabs broadcast
 *   { type: 'mutation:key', idempotencyKey }
 * on `morfi-sdk-cache` BroadcastChannel to seed entries (handled by the
 * cross-tab reconciliation path, file 05 §L2390).
 *
 * USE BY NATS ROUTER
 * ------------------
 * `patch:set` and `invalidate` routing entries dedup against this LRU.
 * `patch:append` BYPASSES — see codegen `appendOrReplaceByIdempotencyKey`.
 */

export const IDEMPOTENCY_LRU_MAX_ENTRIES = 4096;
export const IDEMPOTENCY_LRU_MIN_RETAIN_MS = 90_000;

interface Entry {
  completedAt: number;
}

/**
 * Hybrid time-and-count-bounded LRU. Insertion order is recency; eviction
 * scans from the oldest until either (a) count fits OR (b) the oldest entry
 * is still within the 90 s retention floor (in which case we leave it).
 *
 * The latter rule is load-bearing: under a burst > 4096 fresh mutations the
 * Map briefly exceeds the cap. `has()` and `record()` are the only public
 * surface; readers don't see size invariants between calls.
 */
export class IdempotencyLru {
  private readonly map = new Map<string, Entry>();
  private readonly maxEntries: number;
  private readonly minRetainMs: number;

  constructor(options: { maxEntries?: number; minRetainMs?: number } = {}) {
    this.maxEntries = options.maxEntries ?? IDEMPOTENCY_LRU_MAX_ENTRIES;
    this.minRetainMs = options.minRetainMs ?? IDEMPOTENCY_LRU_MIN_RETAIN_MS;
  }

  /**
   * Stamps `key` with `completedAt = now` and re-orders to MRU. Caller
   * uses this at both `onMutate` (initial insert) and `onSettled`
   * (refresh stamp).
   */
  record(key: string, now: number = Date.now()): void {
    // Re-insert to refresh recency order.
    this.map.delete(key);
    this.map.set(key, { completedAt: now });
    this.evict(now);
  }

  /**
   * Returns true iff `key` is currently in the LRU. Does NOT refresh
   * recency (read-only check).
   */
  has(key: string): boolean {
    return this.map.has(key);
  }

  /**
   * Cross-tab seed: insert without changing now-stamp if already present.
   * Used by the BroadcastChannel handler when a sibling tab announces a
   * fresh mutation key.
   */
  seed(key: string, completedAt: number = Date.now()): void {
    if (this.map.has(key)) return;
    this.map.set(key, { completedAt });
    this.evict(completedAt);
  }

  /** Test seam — current entry count. */
  get size(): number {
    return this.map.size;
  }

  /** Test seam — drop everything. */
  clear(): void {
    this.map.clear();
  }

  private evict(now: number): void {
    if (this.map.size <= this.maxEntries) return;
    // Iterate in insertion order (= oldest first). Stop at the first
    // entry whose age is below the retention floor.
    for (const [key, entry] of this.map) {
      if (this.map.size <= this.maxEntries) return;
      if (now - entry.completedAt <= this.minRetainMs) return;
      this.map.delete(key);
    }
  }
}

// =============================================================================
// T7005 · factory-function surface (canonical spec shape)
// =============================================================================
//
// Spec 05 §idempotency-lru calls for a factory-function constructor whose
// returned value carries only the minimal {insert, touch, has, size}
// surface. This is the surface consumed by the mutation wrapper
// (`useSdkMutation`) and the dispatch layer (`patch:set`/`invalidate`
// suppression). The class above predates this rename — we keep it for the
// existing `record`/`seed` callsites and expose the factory as the
// canonical public API. Both delegate to the same Map internals via
// independent storage (no shared state — factories and the class create
// fresh stores).

export interface IdempotencyEntry {
  readonly completedAt: number;
}

export interface IdempotencyLRU {
  insert(key: string): void;
  touch(key: string, at: number): void;
  has(key: string): boolean;
  size(): number;
}

export interface IdempotencyLRUOptions {
  readonly capacity?: number;
  readonly minRetentionMs?: number;
  readonly now?: () => number;
}

/**
 * Factory form of the hybrid time+count LRU. Returns a closed-over instance
 * with the minimal {insert, touch, has, size} surface.
 *
 * Eviction policy:
 *   - Triggered only when `size > capacity` (post-insert).
 *   - Iterates in insertion order; deletes the oldest entries whose
 *     `completedAt` is more than `minRetentionMs` in the past.
 *   - Stops at the first entry younger than the floor — subsequent entries
 *     are younger still, so the sweep is sound.
 *   - Under burst, may leave the Map briefly above `capacity`. The next
 *     sweep (after the floor lapses) returns to <= capacity.
 *
 * `now` is dependency-injected so tests can pin deterministic frames.
 * Default is `performance.now()` (web monotonic clock).
 */
import { LRUCache } from 'lru-cache';

/**
 * Alternate impl backed by the `lru-cache` npm package. Identical surface
 * to `createIdempotencyLRU` but uses LRUCache's `ttl` + `max` together so
 * eviction tracks both capacity AND age in the underlying data structure
 * (no manual sweep). Useful as a drop-in for downstream consumers that
 * want the canonical npm LRU semantics; the Map-based default below stays
 * the spec-aligned reference impl (hybrid count + retention floor).
 */
export function createPackageBackedIdempotencyLRU(opts?: IdempotencyLRUOptions): IdempotencyLRU {
  const capacity = opts?.capacity ?? IDEMPOTENCY_LRU_MAX_ENTRIES;
  const minRetentionMs = opts?.minRetentionMs ?? IDEMPOTENCY_LRU_MIN_RETAIN_MS;
  const cache = new LRUCache<string, IdempotencyEntry>({
    max: capacity,
    ttl: minRetentionMs,
    ttlAutopurge: false,
    updateAgeOnGet: false,
  });
  const now = opts?.now ?? ((): number => performance.now());
  return {
    insert(key: string): void {
      cache.set(key, { completedAt: now() });
    },
    touch(key: string, at: number): void {
      if (cache.has(key)) cache.set(key, { completedAt: at });
    },
    has(key: string): boolean {
      return cache.has(key);
    },
    size(): number {
      return cache.size;
    },
  };
}

// Devtool peek — same pattern as `pending-auth-retry`. The mutation
// wrapper builds exactly one LRU per running app, so "most recent" is
// "the live one". `getIdempotencyLRU()` returns it (or undefined before
// first construction) so devtool surfaces can call `.size()` without
// dragging the instance through provider context.
const IDEMP_PIN_KEY = '__morfi_idempotency_lru__' as const;
declare global {
  // eslint-disable-next-line no-var
  var __morfi_idempotency_lru__: IdempotencyLRU | undefined;
}
export function getIdempotencyLRU(): IdempotencyLRU | undefined {
  return (globalThis as { [IDEMP_PIN_KEY]?: IdempotencyLRU })[IDEMP_PIN_KEY];
}

export function createIdempotencyLRU(opts?: IdempotencyLRUOptions): IdempotencyLRU {
  const capacity = opts?.capacity ?? IDEMPOTENCY_LRU_MAX_ENTRIES;
  const minRetentionMs = opts?.minRetentionMs ?? IDEMPOTENCY_LRU_MIN_RETAIN_MS;
  const now = opts?.now ?? ((): number => performance.now());
  const map: Map<string, IdempotencyEntry> = new Map();

  function evictIfOver(): void {
    if (map.size <= capacity) return;
    const t = now();
    for (const [k, entry] of map) {
      if (map.size <= capacity) break;
      if (t - entry.completedAt > minRetentionMs) {
        map.delete(k);
      } else {
        // Insertion-order traversal: the first too-young entry implies
        // all subsequent entries are even younger. Stop the sweep.
        break;
      }
    }
  }

  const api: IdempotencyLRU = {
    insert(key: string): void {
      // Re-insertion moves the entry to the end of insertion order so
      // "oldest first" ordering stays correct across the entry's lifetime.
      if (map.has(key)) map.delete(key);
      map.set(key, { completedAt: now() });
      evictIfOver();
    },
    touch(key: string, at: number): void {
      if (!map.has(key)) return;
      map.delete(key);
      map.set(key, { completedAt: at });
    },
    has(key: string): boolean {
      return map.has(key);
    },
    size(): number {
      return map.size;
    },
  };
  (globalThis as { [IDEMP_PIN_KEY]?: IdempotencyLRU })[IDEMP_PIN_KEY] = api;
  return api;
}
